import { Modal, Button, Grid, Paper, ListItem, Typography, Tooltip, Chip } from "@mui/material";
import { useState } from "react";
import { IAircraft } from "@vmosolutions/oc-interfaces";
import { sortAircraftList, getLabel } from "./utils/index";
import { Dispatch, SetStateAction } from "react";
import { SelectField } from "../../../MUI5FormikComponents/SelectField";

interface IAircraftSearchProps {
  aircraft: IAircraft[];
  changeAircraft: Dispatch<SetStateAction<IAircraft | null>>;
  setForm: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  activeAircraft?: IAircraft;
  setStagedAircraft: any;
  setStagedConfig: any;
  setConfigs: any;
}

interface OptionType {
  label: string;
  value: IAircraft | undefined;
}

interface GroupType {
  label: string;
  options: OptionType[];
}

export function AircraftSearch(props: IAircraftSearchProps): JSX.Element {
  const {
    aircraft,
    changeAircraft,
    activeAircraft,
    setForm,
    isLoading,
    setStagedAircraft,
    setStagedConfig,
    setConfigs,
  } = props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const viewListClick: any = (): void => {
    setModalOpen(true);
  };
  const aircraftList: IAircraft[] = sortAircraftList(aircraft);
  const handleAircraftChange = (newValue: { label: string; value: IAircraft }): void => {
    changeAircraft(newValue.value);
    setForm("aircraft");
  };

  const buildOption = (optionValue: IAircraft): OptionType => {
    if (optionValue) {
      return { label: getLabel(optionValue), value: optionValue };
    } else {
      return { label: "", value: undefined };
    }
  };

  const getOptions = (): any => {
    const inactiveOptions: OptionType[] = [];
    const activeOptions: OptionType[] = [];
    const sortedAircraftList = aircraftList.sort(
      (a: IAircraft, b: IAircraft) => -b.aircraftType.title.charAt(0).localeCompare(a.aircraftType.title.charAt(0))
    );

    // Set Label based on preference for aircraft.
    sortedAircraftList.forEach((aircraft: IAircraft) => {
      const optionSyntax = buildOption(aircraft);
      if (aircraft.isActive) {
        activeOptions.push(optionSyntax);
      } else {
        inactiveOptions.push(optionSyntax);
      }
    });

    return [
      { label: "Active", options: activeOptions },
      { label: "Inactive", options: inactiveOptions },
    ];
  };
  return (
    <>
      {!isLoading && aircraft.length > 0 && (
        <>
          <Grid container spacing={2} sx={{ px: 2 }}>
            <Grid item xs={6}>
              <Tooltip title="Add a new aircraft" placement="top">
                <Button
                  variant="contained"
                  disableRipple
                  fullWidth
                  color="primary"
                  onClick={() => {
                    changeAircraft(null);
                    setStagedAircraft();
                    setStagedConfig([]);
                    setConfigs([]);
                    setForm("aircraft");
                  }}
                >
                  Add Aircraft
                </Button>
              </Tooltip>
            </Grid>
            <Grid item xs={6}>
              <Tooltip title="View full aircraft list in a popup" placement="top">
                <Button
                  disableRipple
                  fullWidth
                  color="primary"
                  variant="contained"
                  onClick={viewListClick}
                  sx={{ cursor: "pointer" }}
                >
                  View full list
                </Button>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <SelectField
                name="aircraftSearch"
                label="Available Aircraft"
                options={getOptions()}
                onChange={(newValue) => handleAircraftChange(newValue)}
                value={() => buildOption(activeAircraft)}
              />
            </Grid>
          </Grid>

          <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            aria-labelledby="modal-aircaft-list"
            aria-describedby="modal-aircraft-list-all"
          >
            <Paper sx={{ width: 400, margin: "auto", p: 2 }}>
              <Grid container>
                <Grid item xs={12} sx={{ p: 3, pb: 1 }}>
                  <Typography component="h2">Full List of Aircraft ({aircraft.length} total)</Typography>
                </Grid>
                <Grid item xs={12} sx={{ pb: 2, px: 3 }}>
                  <Typography component="span" sx={{ fontSize: "0.8em" }}>
                    Choose an aircraft to edit
                  </Typography>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item sx={{ maxHeight: "70vh", overflowY: "scroll", width: "100%", mb: 2 }}>
                  {sortAircraftList(aircraft).map((Aircraft: IAircraft, aKey: number) => {
                    return (
                      <ListItem
                        key={`aircraft-${aKey}`}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          transition: "0.2s background-color",
                          "&:hover": { backgroundColor: "#0A1017", transition: "0.5s background-color" },
                        }}
                        onClick={() => {
                          handleAircraftChange({ label: "", value: Aircraft });
                          setStagedAircraft();
                          setStagedConfig([]);
                          setConfigs([]);
                          setForm("aircraft");
                          setModalOpen(false);
                        }}
                      >
                        {getLabel(Aircraft)}
                        <Chip
                          variant="filled"
                          sx={{ borderRadius: 1 }}
                          size="small"
                          color={Aircraft.isActive ? "info" : "error"}
                          label={Aircraft.isActive ? "Active" : "Inactive"}
                        />
                      </ListItem>
                    );
                  })}
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} sx={{ display: "flex", justifyContent: "end" }}>
                  <Button sx={{ alignSelf: "end" }} variant="contained" onClick={() => setModalOpen(false)}>
                    Close
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Modal>
        </>
      )}
    </>
  );
}
